import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { formatNumber } from '../utils/formatNumber';
import { AutomatedBadge, AutoValueBadge, confirmAutomatedValueEdit, isAutomatedMetric } from './AutomationBadge';

const MetricValuesList = () => {
  const [metricValues, setMetricValues] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('value_date');
  const [sortDir, setSortDir] = useState('desc');
  const [metricFilter, setMetricFilter] = useState('');
  const [formData, setFormData] = useState({
    metrics_id: '',
    value_date: '',
    value: '',
    target: '',
    comment: '',
    created_by: 1
  });

  // Fetch data whenever filters, pagination, or sorting changes
  useEffect(() => {
    fetchData();
  }, [page, pageSize, sortBy, sortDir, metricFilter, dateFrom, dateTo, search]);

  // Fetch metrics only once on component mount
  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const metricsResponse = await apiService.metrics.getAll();
      const metricsDataRaw = metricsResponse?.data ?? [];
      const metricsData = Array.isArray(metricsDataRaw.items) ? metricsDataRaw.items
                        : Array.isArray(metricsDataRaw) ? metricsDataRaw
                        : [];
      setMetrics(metricsData);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setMetrics([]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const valuesResponse = await apiService.metricValues.getAll({
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        metric_id: metricFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        q: search || undefined
      });
      
      const valuesData = valuesResponse?.data ?? {};
      const items = Array.isArray(valuesData.items) ? valuesData.items
                 : Array.isArray(valuesData) ? valuesData
                 : [];
      setMetricValues(items);

      const totalCount = Number.isFinite(valuesData.total) ? valuesData.total
                     : Array.isArray(valuesData) ? valuesData.length
                     : 0;
      setTotal(totalCount);

    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching data:', err);
      // make sure arrays stay arrays even on error
      setMetricValues([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        metrics_id: parseInt(formData.metrics_id),
        value_date: formData.value_date,
        value: formData.value ? parseFloat(formData.value) : null,
        target: formData.target ? parseFloat(formData.target) : null,
        comment: formData.comment || null
      };

      if (editingValue) {
        await apiService.metricValues.update(editingValue.id, {
          ...dataToSubmit,
          updated_by: 1
        });
      } else {
        await apiService.metricValues.create(dataToSubmit);
      }
      
      setShowForm(false);
      setEditingValue(null);
      resetFormData();
      fetchData();
    } catch (err) {
      setError('Failed to save metric value');
      console.error('Error saving metric value:', err);
    }
  };

  const calculateDefaultValueDate = async (metricId) => {
    if (!metricId) {
      return new Date().toISOString().split('T')[0]; // Default to today if no metric selected
    }

    try {
      // Fetch the latest metric value for this metric
      const latestValueResponse = await apiService.metricValues.getAll({
        metric_id: metricId,
        sort_by: 'value_date',
        sort_dir: 'desc',
        page: 1,
        page_size: 1
      });

      const latestValue = latestValueResponse?.data?.items?.[0];
      
      if (!latestValue || !latestValue.value_date) {
        return new Date().toISOString().split('T')[0]; // Default to today if no values exist
      }

      // Get the metric to find its frequency
      const selectedMetric = metrics.find(m => m.id === parseInt(metricId));
      const frequency = selectedMetric?.frequency;

      if (!frequency || frequency <= 0) {
        // If no frequency set, default to today
        return new Date().toISOString().split('T')[0];
      }

      // Calculate next date: latest value date + frequency days
      const latestDate = new Date(latestValue.value_date);
      const nextDate = new Date(latestDate);
      nextDate.setDate(nextDate.getDate() + frequency);
      
      return nextDate.toISOString().split('T')[0];
    } catch (err) {
      console.error('Error calculating default value date:', err);
      return new Date().toISOString().split('T')[0]; // Default to today on error
    }
  };

  const resetFormData = () => {
    setFormData({
      metrics_id: '',
      value_date: '',
      value: '',
      target: '',
      comment: '',
      created_by: 1
    });
  };

  const handleEdit = (metricValue) => {
    if (!confirmAutomatedValueEdit(metricValue)) {
      return;
    }
    setEditingValue(metricValue);
    setFormData({
      metrics_id: metricValue.metrics_id.toString(),
      value_date: metricValue.value_date ? metricValue.value_date.split('T')[0] : '',
      value: metricValue.value ? metricValue.value.toString() : '',
      target: metricValue.target ? metricValue.target.toString() : '',
      comment: metricValue.comment || '',
      created_by: metricValue.created_by || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this metric value?')) {
      try {
        await apiService.metricValues.delete(id);
        fetchData();
      } catch (err) {
        setError('Failed to delete metric value');
        console.error('Error deleting metric value:', err);
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingValue(null);
    resetFormData();
  };

  const getMetricName = (metricId) => {
    const metric = metrics.find(m => m.id === metricId);
    if (!metric) {
      return 'Unknown Metric';
    }
    return (
      <>
        {metric.name}
        {isAutomatedMetric(metric) && <AutomatedBadge />}
      </>
    );
  };

  const getMetricUnit = (metricId) => {
    const metric = metrics.find(m => m.id === metricId);
    return metric ? metric.unit : '';
  };

  const getMetricDisplayFormat = (metricId) => {
    const metric = metrics.find(m => m.id === metricId);
    return metric ? metric.display_format : null;
  };

  const formatValue = (value, metricId) => {
    if (value === null || value === undefined) return 'N/A';
    const unit = getMetricUnit(metricId);
    const displayFormat = getMetricDisplayFormat(metricId);
    return formatNumber(value, displayFormat || 'number', unit);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortableHeader = ({ column, currentSort, currentDir, children }) => (
    <th 
      onClick={() => handleSort(column)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {children}
      {currentSort === column && (
        <span style={{ marginLeft: '0.5rem' }}>
          {currentDir === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </th>
  );

  if (loading) return <div className="loading">Loading metric values...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Metric Values</h1>
        <button className="btn btn-primary" onClick={async () => {
          setShowForm(true);
          // If a metric is already selected, calculate default date
          if (formData.metrics_id) {
            const defaultDate = await calculateDefaultValueDate(formData.metrics_id);
            setFormData(prev => ({...prev, value_date: defaultDate}));
          }
        }}>
          Add Metric Value
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="form">
          <h3>{editingValue ? 'Edit Metric Value' : 'Add New Metric Value'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Metric *</label>
                <select
                  className="form-control"
                  value={formData.metrics_id}
                  onChange={async (e) => {
                    const selectedMetricId = e.target.value;
                    const defaultDate = await calculateDefaultValueDate(selectedMetricId);
                    setFormData({...formData, metrics_id: selectedMetricId, value_date: defaultDate});
                  }}
                  required
                >
                  <option value="">Select a metric</option>
                  {metrics.map(metric => (
                    <option key={metric.id} value={metric.id}>
                      {metric.name} {metric.unit ? `(${metric.unit})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Value Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.value_date}
                  onChange={(e) => setFormData({...formData, value_date: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Numeric Value</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  placeholder="Enter numeric value"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Numeric Target</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={formData.target}
                  onChange={(e) => setFormData({...formData, target: e.target.value})}
                  placeholder="Enter target value"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Comment</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.comment}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                placeholder="Add any comments or notes..."
              />
            </div>

            <div style={{display: 'flex', gap: '1rem'}}>
              <button type="submit" className="btn btn-primary">
                {editingValue ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters" style={{display:'grid', gap:'1rem', gridTemplateColumns:'1fr 1fr 1fr 1fr auto', alignItems: 'end'}}>
        <div>
          <label>Metric</label>
          <select value={metricFilter} onChange={e => { setPage(1); setMetricFilter(e.target.value); }}>
          <option value="">All metrics</option>
          {(metrics || []).map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
          </select>
        </div>
        <div>
          <label>Date From</label>
          <input type="date" value={dateFrom} onChange={e => { setPage(1); setDateFrom(e.target.value); }} />
        </div>
        <div>
          <label>Date To</label>
          <input type="date" value={dateTo} onChange={e => { setPage(1); setDateTo(e.target.value); }} />
        </div>
        <div>
          <label>Search</label>
          <input placeholder="Search comment/text…" value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} />
        </div>
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            setPage(1);
            setMetricFilter('');
            setDateFrom('');
            setDateTo('');
            setSearch('');
          }}
          style={{height: 'fit-content'}}
        >
          Clear Filters
        </button>
      </div>
      
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader column="id" currentSort={sortBy} currentDir={sortDir}>ID</SortableHeader>
              <SortableHeader column="metrics_id" currentSort={sortBy} currentDir={sortDir}>Metric</SortableHeader>
              <SortableHeader column="value_date" currentSort={sortBy} currentDir={sortDir}>Date</SortableHeader>
              <SortableHeader column="value" currentSort={sortBy} currentDir={sortDir}>Value</SortableHeader>
              <SortableHeader column="target" currentSort={sortBy} currentDir={sortDir}>Target</SortableHeader>
              <SortableHeader column="comment" currentSort={sortBy} currentDir={sortDir}>Comment</SortableHeader>
              <SortableHeader column="performance" currentSort={sortBy} currentDir={sortDir}>Performance</SortableHeader>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {metricValues.map(metricValue => {
              const actualValue = metricValue.value || 0;
              const targetValue = metricValue.target || 0;
              const performance = targetValue > 0 ? ((actualValue / targetValue) * 100).toFixed(1) + '%' : 'N/A';
              const performanceClass = targetValue > 0 && actualValue >= targetValue ? 'performance-good' : targetValue > 0 && actualValue >= targetValue * 0.8 ? 'performance-warning' : targetValue > 0 ? 'performance-poor' : '';
              
              return (
                <tr key={metricValue.id}>
                  <td>{metricValue.id}</td>
                  <td>{getMetricName(metricValue.metrics_id)}</td>
                  <td>{formatDate(metricValue.value_date)}</td>
                  <td>
                    {metricValue.value !== null ? formatValue(metricValue.value, metricValue.metrics_id) : 'N/A'}
                    {metricValue.is_automated && <AutoValueBadge />}
                  </td>
                  <td>
                    {metricValue.target !== null ? formatValue(metricValue.target, metricValue.metrics_id) : 'N/A'}
                  </td>
                  <td style={{maxWidth: '200px', wordWrap: 'break-word'}}>
                    {metricValue.comment || 'No comment'}
                  </td>
                  <td>
                    <span className={`performance-indicator ${performanceClass}`}>
                      {performance}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-secondary btn-small" 
                      onClick={() => handleEdit(metricValue)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger btn-small" 
                      onClick={() => handleDelete(metricValue.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {metricValues.length === 0 && (
          <div style={{padding: '2rem', textAlign: 'center', color: '#6c757d'}}>
            No metric values found. Add some data to get started!
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div className="pagination" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', padding: '1rem 0'}}>
          <div className="pagination-info">
            Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} results
          </div>
          <div className="pagination-controls" style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
            <select 
              value={pageSize} 
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
              style={{padding: '0.25rem', borderRadius: '4px', border: '1px solid #ccc'}}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <button 
              className="btn btn-secondary" 
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span style={{padding: '0 0.5rem'}}>
              Page {page} of {Math.ceil(total / pageSize)}
            </span>
            <button 
              className="btn btn-secondary" 
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(total / pageSize)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricValuesList;